// Ce qu'il faut à l'écran pour juger une occurrence (D-114) : les périodes et les règles de la semaine que le compte
// voit (les siennes, celles de ses enfants, de son effectif pour le staff), les avances du club pour les matchs, et
// l'occurrence elle-même (heure, domicile, route en cache). Le calcul : availabilityFor (src/domain/availability.ts),
// l'équivalent de la fonction SQL de vérité.
import { clubLeads, toDomainRule, toDomainUnavailability, unavailabilities, unavailabilityRules, type ClubEvent, type MatchRow, type RuleRow, type UnavailabilityRow } from '../lib/api';
import { DEFAULT_LEADS, occInfo, type OccInfo, type WeeklyRule } from '../domain/availability';
import type { AttendanceContext, Unavailability } from '../domain/attendance';

export interface AvailData {
  periods: Unavailability[]; rules: WeeklyRule[];
  periodRows: UnavailabilityRow[]; ruleRows: RuleRow[];
}

export async function loadAvail(): Promise<AvailData> {
  const [periodRows, ruleRows] = await Promise.all([unavailabilities().catch(() => [] as UnavailabilityRow[]), unavailabilityRules().catch(() => [] as RuleRow[])]);
  return { periodRows, ruleRows, periods: periodRows.map(toDomainUnavailability), rules: ruleRows.map(toDomainRule) };
}

export type Leads = { arrive: number; home: number };
export const leadsFor = (clubId: string | null | undefined): Promise<Leads> => (clubId ? clubLeads(clubId) : Promise.resolve(DEFAULT_LEADS));

/** L'occurrence vue par la règle : l'heure du début, domicile ou extérieur, la route en cache. */
export function occOf(ev: Pick<ClubEvent, 'kind'>, o: { date: string; start: Date }, match: Pick<MatchRow, 'is_home' | 'trip'> | null | undefined, leads: Leads): OccInfo {
  return occInfo({ date: o.date, start: o.start, kind: ev.kind, isHome: match?.is_home ?? null, tripMin: match?.trip?.min ?? null }, leads);
}

/** Une note telle qu'écrite, au milieu d'une ligne : « réunion qui peut finir tard » (pas « Réunion… »), sauf un sigle. */
export const inlineNote = (s: string) => (s.length > 1 && s[1] === s[1]!.toLowerCase() ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/** La séance est-elle finie (fin, sinon 2 h après le début) ? Une incertaine compte alors comme présente (D-116). */
export const isPast = (o: { start: Date; end?: Date | null }, now = Date.now()) => (o.end?.getTime() ?? o.start.getTime() + 2 * 3600_000) < now;

/** Le contexte de resolveAttendance pour une occurrence. */
export function attCtx(av: AvailData, ev: Pick<ClubEvent, 'kind'>, o: { date: string; start: Date; end?: Date | null }, match: Pick<MatchRow, 'is_home' | 'trip'> | null | undefined, leads: Leads): AttendanceContext {
  return { date: o.date, unavailabilities: av.periods, rules: av.rules, occ: occOf(ev, o, match, leads), past: isPast(o) };
}
